import React, { useState } from 'react'
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Grid } from '@mui/material'
import { styled } from '@mui/material/styles'
import { motion } from 'framer-motion'
import { categorizeStocks } from './PortfolioUtils'
import { EducationTooltip, getEducationMessages } from './Education'

const AnimatedCard = styled(motion.div)`
  background: #0a0a0a;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(120, 60, 180, 0.3);
  border: 1px solid rgba(165, 121, 255, 0.2);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #8a4fff, #d946ef, #8a4fff);
  }
`

const Title = styled(Typography)`
  font-family: 'Cinzel', serif;
  font-weight: 600;
  letter-spacing: 1px;
  background: linear-gradient(90deg, #ffffff, #d1b3ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  margin-bottom: 24px;
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 2px;
    background: linear-gradient(90deg, #8a4fff, transparent);
    margin: 12px 0 24px;
  }
`

const GradientButton = styled(Button)`
  background: linear-gradient(45deg, #8a4fff, #d946ef);
  color: white;
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  &:hover {
    background: linear-gradient(45deg, #9b5bff, #e557ff);
  }
`

const StockCard = styled(Box)`
  padding: 16px;
  background: rgba(138, 79, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(138, 79, 255, 0.2);
  margin-bottom: 16px;
`

const PortfolioCreator = ({ availableAssets, setPortfolio }) => {
  const [open, setOpen] = useState(false)
  const [strategy, setStrategy] = useState('')
  const strategies = ['aggressive', 'balanced', 'safe']
  const educationMessages = getEducationMessages()

  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    setOpen(false)
    setStrategy('')
  }

  const createPortfolio = () => {
    const categorized = categorizeStocks(availableAssets)
    let selectedStocks = []

    if (strategy === 'aggressive') {
      selectedStocks = categorized.aggressive.slice(0, 5).map(stock => ({
        symbol: stock.symbol,
        amount: 5,
        strategy
      }))
    } else if (strategy === 'balanced') {
      selectedStocks = categorized.balanced.slice(0, 5).map(stock => ({
        symbol: stock.symbol,
        amount: 5,
        strategy
      }))
    } else if (strategy === 'safe') {
      selectedStocks = categorized.safe.slice(0, 5).map(stock => ({
        symbol: stock.symbol,
        amount: 5,
        strategy
      }))
    }

    setPortfolio(selectedStocks)
    handleClose()
  }

  const categorizedStocks = categorizeStocks(availableAssets)

  return (
    <AnimatedCard>
      <Title variant="h4">Portfolio Creator</Title>
      <Typography color="#a0a0a0" fontStyle="italic" sx={{ mb: 2 }}>
        Start building your dividend portfolio with a single click
      </Typography>
      <GradientButton onClick={handleOpen}>
        Create Your Portfolio
      </GradientButton>
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="#b0b0b0">
          Learn how to choose a <EducationTooltip title={educationMessages.strategy}>strategy</EducationTooltip> that fits your goals.
        </Typography>
      </Box>

      <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { background: '#0a0a0a', border: '1px solid rgba(165, 121, 255, 0.2)' } }}>
        <DialogTitle sx={{ color: '#ffffff' }}>
          Choose Your Investment Strategy
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: '#b0b0b0' }}>Strategy</InputLabel>
            <Select
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
              label="Strategy"
              sx={{
                color: '#ffffff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              {strategies.map(s => (
                <MenuItem
                  key={s}
                  value={s}
                  sx={{
                    '&:hover': { backgroundColor: 'rgba(138, 79, 255, 0.1)' },
                    '&.Mui-selected': { backgroundColor: 'rgba(138, 79, 255, 0.2)' }
                  }}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {strategy && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ color: '#e0e0e0', mb: 2 }}>
                Recommended Stocks for {strategy.charAt(0).toUpperCase() + strategy.slice(1)} Strategy
              </Typography>
              <Grid container spacing={2}>
                {strategy === 'aggressive' && categorizedStocks.aggressive.slice(0, 5).map(stock => (
                  <Grid item xs={12} key={stock.symbol}>
                    <StockCard>
                      <Typography variant="body1" sx={{ color: '#ffffff' }}>
                        {stock.symbol}: {stock.yield.toFixed(2)}% Yield
                      </Typography>
                    </StockCard>
                  </Grid>
                ))}
                {strategy === 'balanced' && categorizedStocks.balanced.slice(0, 5).map(stock => (
                  <Grid item xs={12} key={stock.symbol}>
                    <StockCard>
                      <Typography variant="body1" sx={{ color: '#ffffff' }}>
                        {stock.symbol}: {stock.yield.toFixed(2)}% Yield
                      </Typography>
                    </StockCard>
                  </Grid>
                ))}
                {strategy === 'safe' && categorizedStocks.safe.slice(0, 5).map(stock => (
                  <Grid item xs={12} key={stock.symbol}>
                    <StockCard>
                      <Typography variant="body1" sx={{ color: '#ffffff' }}>
                        {stock.symbol}: {stock.yield.toFixed(2)}% Yield
                      </Typography>
                    </StockCard>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} sx={{ color: '#d946ef' }}>
            Cancel
          </Button>
          <GradientButton onClick={createPortfolio} disabled={!strategy}>
            Create Portfolio
          </GradientButton>
        </DialogActions>
      </Dialog>
    </AnimatedCard>
  )
}

export default PortfolioCreator